import { Trans, t } from "@lingui/macro";
import { X, User } from "lucide-react";
import { FC, ReactNode, useEffect, useState } from "react";
import { useAppUIContext } from "../../../context/AppUIContext";
import { Room } from "../../../models/Room";
import { RoomItem } from "../../../models/RoomItem";
import { FilterType } from "../../../models/local/Types";
import { Fire } from "../../../services/FireClient";
import { My } from "../../../services/My";
import { Util } from "../../../services/Utils/Util";
import ItemPopover from "../../components/ItemPopover";
import RoomSettingsModal from "../../components/RoomSettingsModal";
import { PanelListBots } from "./PanelListBots";
import { PanelListImages } from "./PanelListImages";
import { PanelListMembers } from "./PanelListMembers";
import { PanelMemos } from "./PanelMemos";
import { PanelPagecall } from "./PanelPagecall";
import { EnterType } from "./RoomFilesModal";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import { SectionCard, SectionActionButton, SectionListItem } from "./SectionCard";
import { Loader2 } from "lucide-react";
import { useResponsive, hideRightMenu } from "../../../hooks/useResponsive";

export interface IRoomRightMenu {
  type: FilterType,
  room: Room,
  onClose?: () => void;
}
type ItemListTypes = 'officenote' | 'pagecall' | 'files' | 'images' | 'google-drive' | 'links' | 'memos';

export const RoomRightMenu: FC<IRoomRightMenu> = ({ room, type, onClose }) => {
  const { setEnterType, setShowFilesModal } = useAppUIContext();
  const [items] = useState<Map<ItemListTypes, RoomItem[]>>(new Map());
  const [inProgressItems, setInProgressItems] = useState(false);
  const isPersonalRoom = room.visibility === 'personal';
  const { isMobileOrTablet } = useResponsive();

  useEffect(() => {
    hideRightMenu();
    setInProgressItems(true)
    items.clear();
    return Fire.item.subscribeToRecentRoomItems(room.id, (list) => {
      setInProgressItems(true);
      items.clear();
      items.set('officenote', []);
      items.set('pagecall', []);
      items.set('memos', []);
      items.set('files', []);
      items.set('images', []);
      items.set('google-drive', []);
      items.set('links', []);
      list.sort((a, b) => a.pinned && !b.pinned ? -1 : 1).forEach((item) => {
        if (item.type === 'officenote') {
          items.get('officenote')?.push(item);
        } else if (item.type === 'pagecall') {
          items.get('pagecall')?.push(item);
        } else if (item.type === 'memo') {
          items.get('memos')?.push(item);
        } else if (item.type === 'file') {
          if (Util.isImageUrl(item.url))
            items.get('images')?.push(item);
          else
            items.get('files')?.push(item);
        } else if (item.type === 'link') {
          if (item.url?.includes('docs.google.') || item.url?.includes('drive.google.'))
            items.get('google-drive')?.push(item);
          else
            items.get('links')?.push(item);
        }
      });

      setTimeout(() => {
        setInProgressItems(false);
      }, 100);
    });
  }, [room.id])

  function showFilesModal(fileFilter: EnterType) {
    setShowFilesModal(true);
    setEnterType(fileFilter);
  }

  function InvitedEmailList() {
    if (room?.invitedUserEmails && room?.invitedUserEmails?.length > 0) {
      return (
        <SectionCard title={t`Joins on sign-up`} collapsible={false}>
          <div className="flex flex-col">
            {room.invitedUserEmails.map((email) => (
              <SectionListItem key={email}>
                <User className="w-5 h-5 text-gray-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 truncate">{email}</p>
                  <p className="text-xs text-gray-400"><Trans>Unregistered</Trans></p>
                </div>
              </SectionListItem>
            ))}
          </div>
        </SectionCard>
      );
    }
    return null;
  }

  function personalPanel() {
    return (
      <Card className="p-4">
        <p className="text-sm text-gray-500">
          <Trans>This room is only for you.</Trans>
        </p>
      </Card>
    );
  }

  const closeButton = (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => onClose?.()}
      data-cy="close-right-menu"
      className="h-8 w-8 text-gray-400 hover:text-gray-600"
    >
      <X className="w-5 h-5" />
    </Button>
  );

  function RoomSettings({ room }: { room: Room }) {
    return (
      <>
        <RoomSettingsModal room={room} headerAction={isMobileOrTablet ? closeButton : undefined} />
      </>
    );
  }

  return (
    <div className="w-[304px] flex flex-col relative z-10 flex-shrink-0 h-full">
      <div className="flex-1 overflow-y-auto ios-top-margin ios-bottom-padding bg-white md:rounded-panel md:border md:border-[#E5E5E5] overflow-hidden">
        {isMobileOrTablet && (isPersonalRoom || room.isOneToOne) && (
          <div className="sticky top-0 z-10 bg-white p-2 border-b border-gray-100 flex justify-start">
            {closeButton}
          </div>
        )}

        {/* 컨텐츠 영역 */}
        <div className="flex flex-col gap-3">
          {!isPersonalRoom && (
            <>
              {!room.isOneToOne && RoomSettings({ room: room })}
              <PanelListMembers room={room} />
              {(room.workspaceId == "odo_bang" || room.workspaceId == "jiranfamily") && (
                <>
                  <PanelListBots room={room} />
                  <PanelPagecall room={room} items={items.get('pagecall') ?? []} />
                </>
              )}
            </>
          )}

          {isPersonalRoom && personalPanel()}

          <PanelMemos room={room} items={items.get('memos') ?? []} />

          <PanelListImages
            room={room}
            items={items.get('images') ?? []}
            buttons={
              <SectionActionButton
                onClick={() => showFilesModal('images')}
                label={t`View all`}
              />
            }
          />

          <ItemList
            className="documents"
            title={t`Documents`}
            room={room}
            items={items.get('files') ?? []}
            buttons={
              <SectionActionButton
                onClick={() => showFilesModal('documents')}
                label={t`View all`}
              />
            }
          />

          {items.size > 0 && (
            <>
              {(room.workspaceId == "odo_bang" || room.workspaceId == "jiranfamily") && (
                <ItemList
                  className="office-note"
                  title={t`OfficeNote`}
                  room={room}
                  items={items.get('officenote') ?? []}
                  buttons={null}
                />
              )}
              <ItemList
                className="google-drive"
                title={t`Google Drive`}
                room={room}
                items={items.get('google-drive') ?? []}
                buttons={null}
              />
              <ItemList
                className="links"
                title={t`Links`}
                room={room}
                items={items.get('links') ?? []}
                buttons={null}
              />
            </>
          )}

          <InvitedEmailList />

          {inProgressItems && (
            <div className="flex justify-center py-4">
              <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const ContentCard: React.FC<{ content: () => React.ReactNode }> = ({ content }) => {
  return (
    <Card className="p-4">
      {content()}
    </Card>
  );
}

export const RightIframe: React.FC<{ item: RoomItem }> = ({ item }) => {
  const { setIframeItem } = useAppUIContext();

  return (
    <div className="w-[50vw] h-full flex flex-col border-l border-gray-200 z-50 bg-white relative flex-shrink-0">
      <div className="flex items-center gap-2 p-2 border-b">
        <Button variant="ghost" size="icon" onClick={() => setIframeItem(null)}>
          <X className="w-5 h-5" />
        </Button>
        <span className="text-sm truncate">
          {item.title && <span className="font-medium">{item.title} | </span>}
          {item.url}
        </span>
      </div>
      <div className="flex-1">
        <iframe src={item.url} title={item.url} className="w-full h-full border-0" />
      </div>
    </div>
  );
};

// ===========================================================================================================

const ItemList: FC<{ className: string, title: string, room: Room, items: RoomItem[], buttons: ReactNode | null }> = ({ className, title, room, items, buttons }) => {
  const [isCompact, setIsCompact] = useState(true);
  const MAX_COMPACT = 3;
  const [filterKey, setFilterKey] = useState<string | null>(null);
  const [filteredItems, setFilteredItems] = useState<RoomItem[]>([]);

  useEffect(() => {
    setIsCompact(true);
    setFilteredItems(items.slice(0, MAX_COMPACT));
    setFilterKey(null);
  }, [room.id, items])

  function toggleCompact() {
    const _isCompact = !isCompact;
    setIsCompact(_isCompact);
    setFilteredItems(_isCompact ? items.slice(0, MAX_COMPACT) : items);
  }

  function filterList(key: string | null) {
    setFilterKey(key);
    if (key) {
      const k = key.trim().toLowerCase();
      setFilteredItems(items.filter(i => {
        if (i.type == 'file') {
          return i.title?.toLowerCase().includes(k);
        } else {
          return i.url?.toLowerCase().includes(k) || i.title?.toLowerCase().includes(k);
        }
      }));
    } else {
      setFilteredItems(items);
    }
  }

  if (!items.length && className !== 'documents')
    return null;

  return (
    <SectionCard
      title={title}
      count={items.length}
      collapsible={true}
      defaultExpanded={true}
      actions={buttons}
    >
      {/* 필터 입력 (펼쳐진 상태에서만) */}
      {!isCompact && (
        <div className="mb-2 flex items-center gap-2">
          <input
            type="text"
            placeholder={t`Filter list`}
            value={filterKey || ''}
            onChange={(e) => filterList(e.target.value || null)}
            className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-300"
          />
          {filterKey && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => filterList(null)}
              className="h-8 w-8"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}

      {/* 아이템 리스트 */}
      {filteredItems.length > 0 ? (
        <div className="flex flex-col gap-1">
          {filteredItems.map((i) => (
            <div key={i.id}>
              <ItemPopover
                item={i}
                canEdit={[room.creatorId, i.user.id].includes(My.user.id)}
                compact={isCompact}
              />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-400 italic py-3 text-center">
          <Trans>No items</Trans>
        </p>
      )}

      {/* 더보기 버튼 */}
      {isCompact && items.length > MAX_COMPACT && (
        <button
          onClick={toggleCompact}
          className="w-full mt-2 text-sm text-gray-500 hover:text-gray-700 transition-colors py-1"
        >
          <Trans>{items.length - MAX_COMPACT} more items</Trans>
        </button>
      )}
    </SectionCard>
  );
}